import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as authApi from '@/features/auth/api';
import type { SessionSummary } from '@/features/auth/types';
import type { ApiError } from '@/lib/api/client';

/**
 * Session/device management (Phase G-7, Job 9). Deliberately not
 * tenant-scoped - a session is an account-level concept, independent of
 * which organization is currently active, so this query key carries no
 * `TENANT_QUERY_PREFIX` and is untouched by `clearTenantQueries()` on
 * organization switch (switching organizations must not appear to change
 * or lose the account's own device list).
 *
 * Exported so features/auth/session.tsx can remove it specifically on
 * logout/401 - a *different* account logging into the same tab afterward
 * must never see a stale cached device list left over from the previous
 * one. `clearTenantQueries()` would not catch this (this key is
 * deliberately outside that prefix), and the default `staleTime` (30s,
 * app/query-client.ts) is long enough for that stale list to actually be
 * shown as fresh data to the next account within a real login window.
 */
export const AUTH_SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const;
const SESSIONS_KEY = AUTH_SESSIONS_QUERY_KEY;

export function useSessions(): UseQueryResult<readonly SessionSummary[], ApiError> {
  return useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: () => authApi.listSessions(),
  });
}

export function useRevokeSession(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (familyId: string) => authApi.revokeSession(familyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}

export function useRevokeOtherSessions(): UseMutationResult<void, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => authApi.revokeOtherSessions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
}
